import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

export const InventoryPage: React.FC = () => {
  const { colors } = useTheme()

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-inventory'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/inventory-summary`)
      return data
    },
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading inventory analytics...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading data: {(error as Error).message}</p>
          </Card>
        </main>
      </div>
    )
  }

  const summary = data?.summary || {}
  const byCategory = data?.by_category || []
  const lowStock = data?.low_stock_items || []
  const topProducts = data?.top_products || []

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Inventory Summary
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Overview of inventory levels and stock status across the platform
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Products</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: colors.primary }}>
                {(summary.total_products || 0).toLocaleString()}
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Stock Units</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#50C878' }}>
                {(summary.total_stock || 0).toLocaleString()}
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Value</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#7B68EE' }}>
                ${(summary.total_value || 0).toLocaleString()}
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Low Stock Items</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#EF4444' }}>
                {(summary.low_stock_count || 0).toLocaleString()}
              </p>
              <p style={{ fontSize: '12px', color: colors.textSecondary }}>need attention</p>
            </div>
          </Card>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Inventory by Category */}
          <Card>
            <h2 style={{ fontSize: '18px', marginBottom: '16px', color: colors.textPrimary }}>Inventory by Category</h2>
            {byCategory.map((cat: { category: string; product_count: number; total_stock: number; value: number }, idx: number) => (
              <div key={cat.category} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: idx < byCategory.length - 1 ? `1px solid ${colors.border}` : 'none',
              }}>
                <div>
                  <p style={{ fontWeight: 500, color: colors.textPrimary }}>{cat.category}</p>
                  <p style={{ fontSize: '12px', color: colors.textSecondary }}>{cat.product_count} products</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontWeight: 600, color: colors.textPrimary }}>{cat.total_stock.toLocaleString()} units</p>
                  <p style={{ fontSize: '12px', color: colors.textSecondary }}>${cat.value.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </Card>

          {/* Top Products by Stock Value */}
          <Card>
            <h2 style={{ fontSize: '18px', marginBottom: '16px', color: colors.textPrimary }}>Top Products by Value</h2>
            {topProducts.map((product: { name: string; sku: string; stock: number; value: number }, idx: number) => (
              <div key={product.sku} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: idx < topProducts.length - 1 ? `1px solid ${colors.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: colors.primary + '20',
                    color: colors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}>{idx + 1}</span>
                  <div>
                    <p style={{ fontWeight: 500, color: colors.textPrimary, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
                    <p style={{ fontSize: '12px', color: colors.textSecondary }}>{product.sku}</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontWeight: 600, color: '#50C878' }}>${product.value.toLocaleString()}</p>
                  <p style={{ fontSize: '12px', color: colors.textSecondary }}>{product.stock} units</p>
                </div>
              </div>
            ))}
          </Card>
        </div>

        {/* Low Stock Alerts */}
        {lowStock.length > 0 && (
          <Card style={{ border: '1px solid #EF444440' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <h2 style={{ fontSize: '18px', color: '#EF4444' }}>Low Stock Alerts</h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                    <th style={{ textAlign: 'left', padding: '10px', color: colors.textSecondary, fontWeight: 600, fontSize: '13px' }}>Product</th>
                    <th style={{ textAlign: 'left', padding: '10px', color: colors.textSecondary, fontWeight: 600, fontSize: '13px' }}>SKU</th>
                    <th style={{ textAlign: 'left', padding: '10px', color: colors.textSecondary, fontWeight: 600, fontSize: '13px' }}>Seller</th>
                    <th style={{ textAlign: 'right', padding: '10px', color: colors.textSecondary, fontWeight: 600, fontSize: '13px' }}>Current Stock</th>
                    <th style={{ textAlign: 'right', padding: '10px', color: colors.textSecondary, fontWeight: 600, fontSize: '13px' }}>Reorder Level</th>
                    <th style={{ textAlign: 'center', padding: '10px', color: colors.textSecondary, fontWeight: 600, fontSize: '13px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.slice(0, 10).map((item: { name: string; sku: string; seller: string; current_stock: number; reorder_level: number }, idx: number) => (
                    <tr key={item.sku} style={{
                      borderBottom: `1px solid ${colors.border}`,
                      backgroundColor: idx % 2 === 0 ? 'transparent' : colors.background,
                    }}>
                      <td style={{ padding: '12px 10px', color: colors.textPrimary, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</td>
                      <td style={{ padding: '12px 10px', color: colors.textSecondary, fontSize: '13px' }}>{item.sku}</td>
                      <td style={{ padding: '12px 10px', color: colors.textPrimary }}>{item.seller}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#EF4444', fontWeight: 600 }}>{item.current_stock}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: colors.textSecondary }}>{item.reorder_level}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: item.current_stock === 0 ? '#EF444420' : '#F59E0B20',
                          color: item.current_stock === 0 ? '#EF4444' : '#F59E0B',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}>
                          {item.current_stock === 0 ? 'Out of Stock' : 'Low Stock'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
